import { ComponentRef, Injectable, Injector } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { v4 as uuid } from "uuid";
import { DomService } from "../dom.service";
import { IPopup } from "../../../interfaces/i-popup";
import { MapService } from "../map.service";
import { PointInfoPopupComponent } from "../../../../dialog-components/point-info-popup/point-info-popup.component";
import { GroundElevationPopupComponent } from "../../../../dialog-components/ground-elevation-popup/ground-elevation-popup.component";

/**
 * Base class to show html content over map
 * Each viewer with specific logic should create a class extend this
 */
@Injectable()
export class MapOverlaysService {
  dynamicPopupRef: ComponentRef<any>;

  dynamicPointsPopupRef: ComponentRef<any>[] = [];

  constructor(
    protected mapService: MapService,
    protected domService: DomService,
    protected matDialog: MatDialog,
  ) {}

  showOverlayPopUp(
    type: string,
    coordinates: number[],
    epsgCode: string,
    contentHtml: string | HTMLElement,
    onclose?: any,
    featureId?: string,
  ) {
    return this.mapService.getCoreMap().showOverlayPopUp(coordinates, epsgCode, contentHtml, type, onclose, featureId);
  }

  removeAllOverlays(): void {
    this.mapService.getCoreMap().removeAllOverlays();
  }

  removeOverlayPopUp(type: string) {
    this.mapService.getCoreMap().removeOverlayPopUp(type);
  }

  isPopupVisible(popup: IPopup): boolean {
    return this.dynamicPopupRef && this.dynamicPopupRef.instance.type === popup.type;
  }

  showDynamicPopup(popup: IPopup, component: ComponentRef<any>): void {
    if (this.isPopupVisible(popup)) {
      this.dynamicPopupRef.destroy();
      this.domService.destroyComponent(this.dynamicPopupRef);
      this.mapService.getCoreMap().removeRedPinMarkers();
    }
    this.dynamicPopupRef = component;
    const overlayId = `${popup.type}-${uuid()}`;
    this.removeOverlayPopUp(popup.type);
    const divNode = document.createElement("div");
    divNode.id = overlayId;
    this.showOverlayPopUp(
      popup.type,
      popup.coordinates,
      popup.epsg,
      divNode,
      () => {
        this.mapService.getCoreMap().removeRedPinMarkers();
        this.domService.destroyComponent(component);
        if (popup.onClose) popup.onClose();
      },
      popup.featuresData.getId(),
    );
    this.domService.appendComponentRefToDiv(component, `#${overlayId}`);
    component.changeDetectorRef.detectChanges();
  }

  showPointInfoPopup(popup: IPopup, injector: Injector): void {
    const params = {
      matDialog: this.matDialog,
      data: popup,
      injector: injector,
    };
    const pointInfoPopupComponent: ComponentRef<PointInfoPopupComponent> = this.domService.createComponentRef(
      PointInfoPopupComponent,
      params,
    );
    pointInfoPopupComponent.instance.ngZone.run(() => {
      pointInfoPopupComponent.instance.loadComponent(popup);
    });
    this.dynamicPointsPopupRef.push(pointInfoPopupComponent);

    const divNode = document.createElement("div");
    const overlayId = `${popup.type}-${uuid()}`;
    divNode.id = overlayId;
    this.showOverlayPopUp(
      popup.type,
      popup.coordinates,
      popup.epsg,
      divNode,
      () => {
        if (popup.onClose) popup.onClose();
      },
      popup.featuresData.getId(),
    );
    this.domService.appendComponentRefToDiv(pointInfoPopupComponent, `#${overlayId}`);
  }

  removePointInfoPopups() {
    if (this.dynamicPointsPopupRef.length) {
      this.dynamicPointsPopupRef.forEach((ref) => {
        ref.destroy();
        this.domService.destroyComponent(ref);
      });
      this.removeOverlayPopUp("point-info-popup");
    }
  }

  /**
   * Show property data on geographic coordinates
   * @param {IPopup} popup: popup data returned from base component
   */
  showGroundElevationPopup(popup: IPopup): void {
    const params = {
      data: popup.data,
    };
    const groundElevationPopupComponent: ComponentRef<GroundElevationPopupComponent> =
      this.domService.createComponentRef(GroundElevationPopupComponent, params);
    this.showDynamicPopup(popup, groundElevationPopupComponent);
  }
}
